// Utilidad para extraer frames de video basada en el código del usuario
export const extractVideoFrame = (file, timeInSeconds = 1, width = 640, height = 480) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    let hasResolved = false;

    video.addEventListener('loadedmetadata', () => {
      console.log(`✅ Video cargado: ${video.duration}s, ${video.videoWidth}x${video.videoHeight}`);
      const seekTime = Math.min(timeInSeconds, video.duration - 0.1);
      video.currentTime = seekTime;
    });

    video.addEventListener('seeked', () => {
      if (hasResolved) return;
      hasResolved = true;
      
      try {
        ctx.drawImage(video, 0, 0, width, height);
        
        // Convert canvas to blob and create File object
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Error generando blob del frame'));
            return;
          }
          
          const frameFile = new File([blob], `frame_${timeInSeconds}s.jpg`, { 
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          
          console.log(`📸 Frame extraído en ${video.currentTime}s como File object`);
          
          // Cleanup
          video.removeAttribute('src');
          video.load();
          URL.revokeObjectURL(video.src);
          
          resolve(frameFile);
        }, 'image/jpeg', 0.9);
      } catch (err) {
        reject(new Error('Error dibujando frame: ' + err.message));
      }
    });

    video.addEventListener('error', (e) => {
      if (hasResolved) return;
      hasResolved = true;
      console.error('❌ Error en video:', e);
      reject(new Error('Error cargando video'));
    });

    video.muted = true;
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
    
    // Usar Blob URL
    const url = URL.createObjectURL(file);
    video.src = url;
  });
};

// Función para obtener información del video
export const getVideoInfo = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    
    let hasResolved = false;
    let timeoutId;
    
    timeoutId = setTimeout(() => {
      if (hasResolved) return;
      hasResolved = true;
      resolve({
        duration: 60,
        width: 1920,
        height: 1080,
        size: file.size,
        type: file.type,
        name: file.name
      });
    }, 10000);
    
    video.addEventListener('loadedmetadata', () => {
      if (hasResolved) return;
      hasResolved = true;
      clearTimeout(timeoutId);
      
      const info = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        size: file.size,
        type: file.type,
        name: file.name
      };
      
      console.log('📊 Info del video:', info);
      
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(video.src);
      
      resolve(info);
    });

    video.addEventListener('error', () => {
      if (hasResolved) return;
      hasResolved = true;
      clearTimeout(timeoutId);
      
      resolve({
        duration: 60,
        width: 1920,
        height: 1080,
        size: file.size,
        type: file.type,
        name: file.name
      });
    });

    video.preload = 'metadata';
    video.muted = true;
    const url = URL.createObjectURL(file);
    video.src = url;
  });
};

// Formatear duración en formato mm:ss
export const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Formatear tamaño de archivo
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Extraer frame desde URL de video - versión simplificada basada en la que funciona
export const extractVideoFrameFromUrl = (videoUrl, timeInSeconds = 1, width = 640, height = 480) => {
  return new Promise((resolve, reject) => {
    console.log('Starting frame extraction from URL for second:', timeInSeconds);

    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    let hasResolved = false;

    video.addEventListener('loadedmetadata', () => {
      console.log(`✅ Video loaded: ${video.duration}s, ${video.videoWidth}x${video.videoHeight}`);
      const seekTime = Math.min(timeInSeconds, video.duration - 0.1);
      video.currentTime = seekTime;
    });

    video.addEventListener('seeked', () => {
      if (hasResolved) return;
      hasResolved = true;

      try {
        ctx.drawImage(video, 0, 0, width, height);

        // Convert canvas to blob and create File object
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Error generando blob del frame'));
            return;
          }

          const frameFile = new File([blob], `frame_${timeInSeconds}s.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });

          console.log(`📸 Frame extraído en ${video.currentTime}s como File object`);

          // Cleanup
          video.removeAttribute('src');
          video.load();

          resolve(frameFile);
        }, 'image/jpeg', 0.9);
      } catch (err) {
        reject(new Error('Error dibujando frame: ' + err.message));
      }
    });

    video.addEventListener('error', (e) => {
      if (hasResolved) return;
      hasResolved = true;
      console.error('❌ Error en video:', e);
      reject(new Error('Error cargando video'));
    });

    video.muted = true;
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';

    // Para URL directa de Azure Blob Storage
    video.src = videoUrl;
  });
};

// Obtener información de video desde URL usando async/await sin setTimeout
export const getVideoInfoFromUrl = (videoUrl) => {
  return new Promise((resolve, reject) => {
    console.log('Loading video info for URL:', videoUrl);

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.preload = 'metadata';

    let hasResolved = false;

    const onMetadataLoaded = () => {
      if (hasResolved) return;
      hasResolved = true;

      const info = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight
      };

      console.log('📊 Info del video:', info);

      // Cleanup
      video.removeEventListener('loadedmetadata', onMetadataLoaded);
      video.removeEventListener('error', onError);
      video.removeAttribute('src');
      video.load();

      resolve(info);
    };

    const onError = (error) => {
      if (hasResolved) return;
      hasResolved = true;

      console.error('❌ Error loading video info:', error);
      video.removeEventListener('loadedmetadata', onMetadataLoaded);
      video.removeEventListener('error', onError);

      reject(new Error(`Failed to load video info: ${video.error?.message || 'Unknown error'}`));
    };

    video.addEventListener('loadedmetadata', onMetadataLoaded);
    video.addEventListener('error', onError);

    video.src = videoUrl;
  });
};